// src/pages/AdminPage/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '../../firebase/config';
import QRCode from 'qrcode';
import LEKudluLogo from '../../assets/images/LEKudluLogoTSP.svg';
import AbhyaasLogo from '../../assets/images/AbhyaasLogoSquareTransparent.svg';
import './AdminPage.css';

const AdminDashboard = () => {
    const [registrations, setRegistrations] = useState([]);
    const [filteredRegistrations, setFilteredRegistrations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [scannerActive, setScannerActive] = useState(false);
    const [scannerInitialized, setScannerInitialized] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [activeTab, setActiveTab] = useState('scanner'); // scanner, list, stats, luckydraw
    const [stats, setStats] = useState({
        total: 0,
        teachers: 0,
        parents: 0,
        checkedIn: 0,
        pending: 0,
        checkInRate: 0
    });

    // Lucky Draw State
    const [luckyDrawFilter, setLuckyDrawFilter] = useState('all'); // all, parents, teachers, stallowners
    const [isShuffling, setIsShuffling] = useState(false);
    const [winner, setWinner] = useState(null);
    const [winners, setWinners] = useState([]);
    const [showConfetti, setShowConfetti] = useState(false);
    const [showAllCards, setShowAllCards] = useState(false);
    const [allParticipantCards, setAllParticipantCards] = useState([]);
    const [showCardBacks, setShowCardBacks] = useState(false);
    const [pickedCardId, setPickedCardId] = useState(null);
    const [revealingWinner, setRevealingWinner] = useState(false);

    // Stall Owner State
    const [stallOwners, setStallOwners] = useState([]);
    const [stallOwnerWinners, setStallOwnerWinners] = useState([]);

    // Load registrations and stall owners from Firestore
    useEffect(() => {
        const q1 = query(collection(db, 'carnival_registrations'), orderBy('registeredAt', 'desc'));
        const q2 = query(collection(db, 'stall_owner_registrations'), orderBy('registeredAt', 'desc'));

        const unsubscribe1 = onSnapshot(q1, (snapshot) => {
            const carnivalData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                type: 'carnival' // Mark as carnival registration
            }));

            // Merge with stall owners and update registrations
            const unsubscribe2 = onSnapshot(q2, (stallSnapshot) => {
                const stallOwnersData = stallSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    type: 'stallowner', // Mark as stall owner
                    parentName: doc.data().stallOwnerName, // Map for lucky draw compatibility
                    childName: doc.data().stallName, // Map stall name as child name
                    phone: doc.data().phoneNumber, // Map phoneNumber to phone for consistency
                }));

                setStallOwners(stallOwnersData);

                // Merge both lists
                const allData = [...carnivalData, ...stallOwnersData];
                setRegistrations(allData);
                setFilteredRegistrations(allData);

                // Calculate stats
                const total = carnivalData.length;
                const teachers = carnivalData.filter(r => r.isTeacher).length;
                const parents = total - teachers;
                const checkedIn = carnivalData.filter(r => r.checkedIn).length;
                const stallOwnersCount = stallOwnersData.length;
                // Check-in rate only counts parents (teachers don't need to check in)
                const checkInRate = parents > 0 ? Math.round((checkedIn / parents) * 100) : 0;

                setStats({
                    total,
                    teachers,
                    parents,
                    checkedIn,
                    pending: parents - checkedIn,
                    checkInRate,
                    stallOwners: stallOwnersCount
                });
            });

            return () => unsubscribe2();
        });

        return () => unsubscribe1();
    }, []);

    // Filter registrations by search
    useEffect(() => {
        if (!searchTerm) {
            setFilteredRegistrations(registrations);
        } else {
            const filtered = registrations.filter(r =>
                r.parentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                r.childName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                r.phone.includes(searchTerm) ||
                r.registrationId.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredRegistrations(filtered);
        }
    }, [searchTerm, registrations]);

    // Initialize QR Scanner (optimized for ALL conditions)
    useEffect(() => {
        let scanner = null;

        if (scannerActive && !scannerInitialized) {
            // Slight delay to ensure DOM is ready
            setTimeout(() => {
                scanner = new Html5QrcodeScanner(
                    "qr-reader",
                    {
                        fps: 5, // Lower FPS for better processing on low-quality cameras
                        qrbox: { width: 300, height: 300 }, // Larger box for easier scanning
                        aspectRatio: 1.0,
                        showTorchButtonIfSupported: true,
                        showZoomSliderIfSupported: true,
                        defaultZoomValueIfSupported: 2,
                        supportedScanTypes: [0], // Camera only, no file upload
                        videoConstraints: {
                            facingMode: "environment", // Use back camera
                            advanced: [
                                { focusMode: "continuous" },
                                { exposureMode: "continuous" },
                                { whiteBalanceMode: "continuous" },
                                { zoom: 1.5 }
                            ]
                        },
                        // Support ALL barcode formats for maximum compatibility
                        formatsToSupport: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                        // Experimental features for better scanning
                        experimentalFeatures: {
                            useBarCodeDetectorIfSupported: true
                        },
                        rememberLastUsedCamera: true
                    },
                    /* verbose= */ false
                );

                scanner.render(onScanSuccess, onScanError);
                setScannerInitialized(true);
            }, 300);
        }

        return () => {
            if (scanner) {
                scanner.clear().catch(console.error);
                setScannerInitialized(false);
            }
        };
    }, [scannerActive]);

    const onScanSuccess = async (decodedText) => {
        try {
            const scannedData = JSON.parse(decodedText);
            const registrationId = scannedData.id;

            // Find registration
            const registration = registrations.find(r => r.registrationId === registrationId);

            if (!registration) {
                setScanResult({
                    success: false,
                    message: 'Registration not found!'
                });
                return;
            }

            if (registration.checkedIn) {
                setScanResult({
                    success: false,
                    alreadyCheckedIn: true,
                    message: `✓ Already Checked In`,
                    submessage: `Checked in at ${new Date(registration.checkedInAt).toLocaleTimeString()}`,
                    data: registration
                });
                setTimeout(() => setScanResult(null), 4000);
                return;
            }

            // Mark as checked in
            await checkInRegistration(registration.id);

            setScanResult({
                success: true,
                message: 'Check-in successful!',
                data: registration
            });

            // Auto-clear result after 3 seconds
            setTimeout(() => setScanResult(null), 3000);
        } catch (err) {
            console.error('Scan Error:', err);
            setScanResult({
                success: false,
                message: 'Invalid QR code!'
            });
        }
    };

    const onScanError = (errorMessage) => {
        // Ignore scan errors (they happen frequently)
    };

    const checkInRegistration = async (docId) => {
        try {
            const docRef = doc(db, 'carnival_registrations', docId);
            await updateDoc(docRef, {
                checkedIn: true,
                checkedInAt: new Date().toISOString()
            });
        } catch (err) {
            console.error('Check-in Error:', err);
            throw err;
        }
    };

    const handleManualCheckIn = async (registration) => {
        try {
            await checkInRegistration(registration.id);
            alert(`${registration.parentName} checked in successfully!`);
        } catch (err) {
            alert('Check-in failed. Please try again.');
        }
    };

    const toggleTeacherStatus = async (registration) => {
        try {
            const docRef = doc(db, 'carnival_registrations', registration.id);
            await updateDoc(docRef, {
                isTeacher: !registration.isTeacher
            });
        } catch (err) {
            console.error('Toggle Teacher Error:', err);
            alert('Failed to update teacher status. Please try again.');
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            window.location.reload();
        } catch (err) {
            console.error('Logout Error:', err);
        }
    };

    // Delete registration with passcode
    const handleDeleteRegistration = async (registration) => {
        const passcode = prompt('Enter admin passcode to delete this registration:');

        if (passcode !== '2662') {
            alert('Invalid passcode! Only admin can delete registrations.');
            return;
        }

        const confirmDelete = window.confirm(`Are you sure you want to delete registration for ${registration.parentName}? This cannot be undone!`);

        if (!confirmDelete) return;

        try {
            // Delete from the correct collection based on type
            const collectionName = registration.type === 'stallowner' ? 'stall_owner_registrations' : 'carnival_registrations';
            await deleteDoc(doc(db, collectionName, registration.id));
            alert('Registration deleted successfully!');
        } catch (err) {
            console.error('Delete Error:', err);
            alert('Failed to delete registration. Please try again.');
        }
    };

    // Generate pass for a specific registration
    const generatePassForUser = async (registration) => {
        try {
            // Generate QR Code
            const qrData = JSON.stringify({
                id: registration.registrationId,
                parentName: registration.parentName,
                childName: registration.childName,
                event: 'Deepawali Carnival 2025',
                venue: 'Little Elly Kudlu'
            });

            const qrCode = await QRCode.toDataURL(qrData, {
                width: 400,
                margin: 2,
                color: {
                    dark: '#1A1423',
                    light: '#FFFFFF'
                }
            });

            // Create canvas
            const canvas = await generatePassCanvas(registration, qrCode);

            // Download as image
            const link = document.createElement('a');
            link.download = `Pass_${registration.parentName.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL('image/png', 1.0);
            link.click();
        } catch (err) {
            console.error('Pass generation error:', err);
            alert('Failed to generate pass. Please try again.');
        }
    };

    const generatePassCanvas = async (registration, qrCode) => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            canvas.width = 1080;
            canvas.height = 1920;
            const ctx = canvas.getContext('2d');

            // Background gradient
            const bgGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
            bgGradient.addColorStop(0, '#1A1423');
            bgGradient.addColorStop(0.3, '#5B2C6F');
            bgGradient.addColorStop(0.6, '#C2185B');
            bgGradient.addColorStop(1, '#FF6B35');
            ctx.fillStyle = bgGradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Decorative border
            ctx.strokeStyle = '#FDB750';
            ctx.lineWidth = 12;
            ctx.strokeRect(40, 40, canvas.width - 80, canvas.height - 80);

            ctx.strokeStyle = 'rgba(253, 183, 80, 0.4)';
            ctx.lineWidth = 8;
            ctx.strokeRect(60, 60, canvas.width - 120, canvas.height - 120);

            // Load and draw LE Kudlu logo at top
            const logoImg = new Image();
            logoImg.crossOrigin = 'anonymous';
            logoImg.onload = () => {
                // Draw logo at top center - SMALLER, MORE SPACE
                const logoSize = 110;
                const logoX = (canvas.width - logoSize) / 2;
                const logoY = 100;
                ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);

                // Title - MORE SPACE FROM LOGO
                const headerY = 280;
                ctx.fillStyle = '#FDB750';
                ctx.font = 'bold 70px Arial, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText('Deepawali Carnival', canvas.width / 2, headerY);

                ctx.fillStyle = '#FFFFFF';
                ctx.font = 'bold 38px Arial, sans-serif';
                ctx.fillText('2025', canvas.width / 2, headerY + 52);

                // Diya emoji decorations - NOT CLASHING
                ctx.font = '48px Arial';
                ctx.fillText('🪔', 150, headerY + 15);
                ctx.fillText('🪔', canvas.width - 150, headerY + 15);

                // Guest details - MORE SPACE
                const detailsY = 420;
                ctx.fillStyle = 'rgba(45, 27, 61, 0.9)';
                roundRect(ctx, 100, detailsY, canvas.width - 200, 450, 30, true);

                ctx.fillStyle = '#FDB750';
                ctx.font = 'bold 48px Arial, sans-serif';
                ctx.textAlign = 'left';
                ctx.fillText('🎉 Guest Details', 160, detailsY + 70);

                ctx.fillStyle = '#FFFFFF';
                ctx.font = '38px Arial, sans-serif';

                let yOffset = detailsY + 150;
                ctx.fillText('Parent:', 160, yOffset);
                ctx.font = 'bold 38px Arial, sans-serif';
                ctx.fillStyle = '#FDB750';
                ctx.fillText(registration.parentName, 160, yOffset + 50);

                yOffset += 130;
                ctx.fillStyle = '#FFFFFF';
                ctx.font = '38px Arial, sans-serif';
                ctx.fillText('Child:', 160, yOffset);
                ctx.font = 'bold 38px Arial, sans-serif';
                ctx.fillStyle = '#FDB750';
                ctx.fillText(registration.childName, 160, yOffset + 50);

                yOffset += 130;
                ctx.fillStyle = '#FFFFFF';
                ctx.font = '32px Arial, sans-serif';
                ctx.fillText(`ID: ${registration.registrationId}`, 160, yOffset);

                // Event details
                const eventY = 900;
                ctx.fillStyle = 'rgba(255, 107, 53, 0.3)';
                roundRect(ctx, 100, eventY, canvas.width - 200, 280, 30, true);

                ctx.fillStyle = '#FDB750';
                ctx.font = 'bold 44px Arial, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText('📅 Event Details', canvas.width / 2, eventY + 60);

                ctx.fillStyle = '#FFFFFF';
                ctx.font = '36px Arial, sans-serif';
                ctx.fillText('November 1st, 2025 | 10:30 AM onwards', canvas.width / 2, eventY + 130);

                ctx.font = 'bold 38px Arial, sans-serif';
                ctx.fillStyle = '#FDB750';
                ctx.fillText('Little Elly Kudlu Campus', canvas.width / 2, eventY + 200);

                // QR Code - PROPER SPACING
                const qrY = 1230;
                const qrSize = 350;
                const qrX = (canvas.width - qrSize) / 2;

                ctx.fillStyle = '#FFFFFF';
                ctx.shadowColor = 'rgba(253, 183, 80, 0.6)';
                ctx.shadowBlur = 30;
                roundRect(ctx, qrX - 15, qrY - 15, qrSize + 30, qrSize + 30, 20, true);
                ctx.shadowBlur = 0;

                const qrImage = new Image();
                qrImage.onload = () => {
                    ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

                    ctx.fillStyle = '#FFFFFF';
                    ctx.font = '32px Arial, sans-serif';
                    ctx.textAlign = 'center';
                    ctx.fillText('Scan at venue entrance', canvas.width / 2, qrY + qrSize + 55);

                    // Footer - PROPER SPACING
                    const footerY = canvas.height - 130;
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                    ctx.font = '28px Arial, sans-serif';
                    ctx.fillText('Organized by Little Elly Kudlu', canvas.width / 2, footerY);

                    ctx.fillStyle = 'rgba(253, 183, 80, 0.7)';
                    ctx.font = 'bold 24px Arial, sans-serif';
                    ctx.fillText('Crafted by neuralway.ai', canvas.width / 2, footerY + 50);

                    resolve(canvas);
                };
                qrImage.src = qrCode;
            };

            logoImg.src = LEKudluLogo;
        });
    };

    const roundRect = (ctx, x, y, width, height, radius, fill) => {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
        if (fill) ctx.fill();
    };

    // Lucky Draw Functions
    const getEligibleParticipants = () => {
        let eligible = registrations.filter(r => !winners.find(w => w.id === r.id));

        if (luckyDrawFilter === 'parents') {
            // Only parents (not teachers, not stall owners)
            eligible = eligible.filter(r => !r.isTeacher && r.type === 'carnival');
        } else if (luckyDrawFilter === 'teachers') {
            // Only teachers
            eligible = eligible.filter(r => r.isTeacher && r.type === 'carnival');
        } else if (luckyDrawFilter === 'stallowners') {
            // Only stall owners
            eligible = eligible.filter(r => r.type === 'stallowner');
        }
        // 'all' returns everyone

        return eligible;
    };

    // Show all cards first
    const displayAllCards = () => {
        const eligible = getEligibleParticipants();
        if (eligible.length === 0) {
            alert('No eligible participants!');
            return;
        }

        setAllParticipantCards(eligible);
        setShowAllCards(true);
        setWinner(null);
        setShowCardBacks(false);
        setPickedCardId(null);
        setRevealingWinner(false);
    };

    const startLuckyDraw = () => {
        const eligible = getEligibleParticipants();

        if (eligible.length === 0) {
            alert('No eligible participants left!');
            return;
        }

        // If cards not shown yet, show them first
        if (!showAllCards) {
            displayAllCards();
            return;
        }

        // Pick winner FIRST to ensure consistency
        const winnerIndex = Math.floor(Math.random() * eligible.length);
        const selectedWinner = eligible[winnerIndex];

        // Animation sequence:
        // 1. IMMEDIATELY flip all cards face-down (1.5 seconds)
        // 2. Shuffle with cards face-down for 7 seconds - NO NAMES VISIBLE AT ALL
        // 3. Pick one card (2 seconds)
        // 4. Dramatic pause (0.5 seconds)
        // 5. Flip picked card to reveal winner (2 seconds)
        // 6. Show full winner details (2 seconds)

        setIsShuffling(true);
        setWinner(null);
        setShowConfetti(false);

        // Step 1: IMMEDIATELY flip cards face-down (no pre-shuffle with names visible!)
        setShowCardBacks(true);

        // Step 2: Start shuffling AFTER cards are flipped (all face-down, NO NAMES)
        setTimeout(() => {
            // Shuffle cards with DRAMATIC visible movement - cards stay face-down
            let shuffleCount = 0;
            const shuffleInterval = setInterval(() => {
                setAllParticipantCards(prev => [...prev].sort(() => Math.random() - 0.5));
                shuffleCount++;

                // After 10 seconds of face-down shuffling, pick a card
                if (shuffleCount >= 20) {
                    clearInterval(shuffleInterval);

                    // Step 3: Pick the winner card (slower)
                    setTimeout(() => {
                        setPickedCardId(selectedWinner.id);

                        // Step 4: Dramatic pause before reveal
                        setTimeout(() => {

                            // Step 5: Flip picked card to reveal (much slower)
                            setTimeout(() => {
                                setRevealingWinner(true);

                                // Step 6: Show full winner details
                                setTimeout(() => {
                                    setWinner(selectedWinner);
                                    setWinners(prev => [...prev, selectedWinner]);
                                    setIsShuffling(false);
                                    setShowAllCards(false);
                                    setShowCardBacks(false);
                                    setPickedCardId(null);
                                    setRevealingWinner(false);
                                    setShowConfetti(true);

                                    // Hide confetti after 6 seconds
                                    setTimeout(() => setShowConfetti(false), 6000);
                                }, 2000);
                            }, 500);
                        }, 2000);
                    }, 2000);
                }
            }, 500); // 500ms between shuffles - SUPER slow, clearly visible card movement
        }, 1500); // Wait for flip animation to complete
    };

    const resetWinners = () => {
        if (window.confirm('Are you sure you want to reset all winners? This cannot be undone!')) {
            setWinners([]);
            setWinner(null);
            setShowConfetti(false);
            setShowAllCards(false);
            setAllParticipantCards([]);
        }
    };

    const removeWinner = (winnerId) => {
        if (window.confirm('Remove this winner from the list?')) {
            setWinners(prev => prev.filter(w => w.id !== winnerId));
            if (winner && winner.id === winnerId) {
                setWinner(null);
            }
        }
    };

    // Download all stats as CSV
    const downloadStatsCSV = () => {
        const csv = [
            ['Registration ID', 'Parent Name', 'Child Name', 'Phone', 'Email', 'Registered At', 'Checked In', 'Checked In At'],
            ...registrations.map(r => [
                r.registrationId,
                r.parentName,
                r.childName,
                r.phone,
                r.email || '',
                r.registeredAt,
                r.checkedIn ? 'Yes' : 'No',
                r.checkedInAt || ''
            ])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Carnival_Registrations_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <h1>Admin Dashboard</h1>
                <button className="logout-button" onClick={handleLogout}>
                    Logout →
                </button>
            </div>

            {/* Tabs */}
            <div className="admin-tabs">
                <button
                    className={`tab ${activeTab === 'scanner' ? 'active' : ''}`}
                    onClick={() => {
                        setActiveTab('scanner');
                        setScannerActive(false);
                        setScannerInitialized(false);
                    }}
                >
                    📷 QR Scanner
                </button>
                <button
                    className={`tab ${activeTab === 'list' ? 'active' : ''}`}
                    onClick={() => {
                        setActiveTab('list');
                        setScannerActive(false);
                    }}
                >
                    📋 Registrations
                </button>
                <button
                    className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
                    onClick={() => {
                        setActiveTab('stats');
                        setScannerActive(false);
                    }}
                >
                    📊 Stats
                </button>
                <button
                    className={`tab ${activeTab === 'luckydraw' ? 'active' : ''}`}
                    onClick={() => {
                        setActiveTab('luckydraw');
                        setScannerActive(false);
                    }}
                >
                    🎁 Lucky Draw
                </button>
            </div>

            {/* Content */}
            <AnimatePresence mode="wait">
                {activeTab === 'scanner' && (
                    <motion.div
                        key="scanner"
                        className="tab-content"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                    >
                        <div className="scanner-section">
                            <div className="scanner-instructions-header">
                                <h3>📱 QR Code Scanner</h3>
                                <div className="scanner-tips">
                                    <p>• Click START CAMERA button below</p>
                                    <p>• Allow camera permissions when prompted</p>
                                    <p>• Hold phone steady - works in ANY lighting condition</p>
                                    <p>• Optimized for low-quality cameras and glare</p>
                                </div>
                            </div>

                            {!scannerActive && (
                                <motion.button
                                    className="start-camera-button"
                                    onClick={() => setScannerActive(true)}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <span className="camera-icon">📷</span>
                                    START CAMERA
                                </motion.button>
                            )}

                            <div id="qr-reader"></div>

                            {scannerActive && (
                                <motion.button
                                    className="stop-camera-button"
                                    onClick={() => {
                                        setScannerActive(false);
                                        setScannerInitialized(false);
                                    }}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <span className="stop-icon">⏹</span>
                                    STOP CAMERA
                                </motion.button>
                            )}

                            {/* Prominent Scan Result Overlay */}
                            <AnimatePresence>
                                {scanResult && (
                                    <motion.div
                                        className="scan-result-overlay"
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.5 }}
                                        transition={{ type: "spring", duration: 0.5 }}
                                    >
                                        <motion.div
                                            className={`scan-result-card ${scanResult.success ? 'success' : scanResult.alreadyCheckedIn ? 'warning' : 'error'}`}
                                            initial={{ y: 50 }}
                                            animate={{ y: 0 }}
                                        >
                                            <div className="result-icon-large">
                                                {scanResult.success ? '✓' : scanResult.alreadyCheckedIn ? '⚠' : '✗'}
                                            </div>
                                            <h2 className="result-message">{scanResult.message}</h2>
                                            {scanResult.submessage && (
                                                <p className="result-submessage">{scanResult.submessage}</p>
                                            )}
                                            {scanResult.data && (
                                                <div className="result-details-card">
                                                    <div className="detail-row">
                                                        <span className="detail-label">Parent:</span>
                                                        <span className="detail-value">{scanResult.data.parentName}</span>
                                                    </div>
                                                    <div className="detail-row">
                                                        <span className="detail-label">Child:</span>
                                                        <span className="detail-value">{scanResult.data.childName}</span>
                                                    </div>
                                                    <div className="detail-row">
                                                        <span className="detail-label">Phone:</span>
                                                        <span className="detail-value">{scanResult.data.phone}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </motion.div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                )}

                {activeTab === 'list' && (
                    <motion.div
                        key="list"
                        className="tab-content"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                    >
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search by name, phone, or ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="registrations-list">
                            {filteredRegistrations.map((registration) => (
                                <motion.div
                                    key={registration.id}
                                    className={`registration-card ${registration.checkedIn ? 'checked-in' : ''} ${registration.isTeacher ? 'teacher' : ''} ${registration.type === 'stallowner' ? 'stallowner' : ''}`}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    layout
                                >
                                    <div className="registration-info">
                                        <div className="name-with-badge">
                                            <h3>{registration.parentName}</h3>
                                            {registration.isTeacher && registration.type !== 'stallowner' && (
                                                <span className="teacher-badge">👩‍🏫 Teacher</span>
                                            )}
                                            {registration.type === 'stallowner' && (
                                                <span className="stallowner-badge">🎪 Stall Owner</span>
                                            )}
                                        </div>
                                        {registration.type === 'stallowner' ? (
                                            <>
                                                <p>Stall: {registration.childName}</p>
                                                <p>Phone: {registration.phone}</p>
                                            </>
                                        ) : (
                                            <>
                                                <p>Child: {registration.childName}</p>
                                                <p>Phone: {registration.phone}</p>
                                                <p className="reg-id">ID: {registration.registrationId || registration.id.substring(0, 8)}</p>
                                            </>
                                        )}
                                    </div>

                                    <div className="registration-actions">
                                        <button
                                            className={`teacher-toggle-btn ${registration.isTeacher ? 'active' : ''}`}
                                            onClick={() => toggleTeacherStatus(registration)}
                                            title={registration.isTeacher ? "Mark as Parent" : "Mark as Teacher"}
                                        >
                                            {registration.isTeacher ? '👩‍🏫' : '👨‍👩‍👧'}
                                        </button>

                                        <button
                                            className="download-pass-button"
                                            onClick={() => generatePassForUser(registration)}
                                            title="Download Pass"
                                        >
                                            📥 Pass
                                        </button>

                                        <button
                                            className="delete-button"
                                            onClick={() => handleDeleteRegistration(registration)}
                                            title="Delete Registration (Passcode Required)"
                                        >
                                            🗑️
                                        </button>

                                        {registration.checkedIn ? (
                                            <div className="status-badge success">
                                                ✓ Checked In
                                                <small>{new Date(registration.checkedInAt).toLocaleTimeString()}</small>
                                            </div>
                                        ) : (
                                            <button
                                                className="checkin-button"
                                                onClick={() => handleManualCheckIn(registration)}
                                            >
                                                Check In
                                            </button>
                                        )}
                                    </div>
                                </motion.div>
                            ))}

                            {filteredRegistrations.length === 0 && (
                                <div className="empty-state">
                                    <p>No registrations found</p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}

                {activeTab === 'stats' && (
                    <motion.div
                        key="stats"
                        className="tab-content"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                    >
                        <div className="stats-grid">
                            <div className="stat-card total">
                                <div className="stat-icon">📊</div>
                                <div className="stat-value">{stats.total}</div>
                                <div className="stat-label">Total Registrations</div>
                            </div>

                            <div className="stat-card parents">
                                <div className="stat-icon">👨‍👩‍👧</div>
                                <div className="stat-value">{stats.parents}</div>
                                <div className="stat-label">Parents</div>
                            </div>

                            <div className="stat-card teachers">
                                <div className="stat-icon">👩‍🏫</div>
                                <div className="stat-value">{stats.teachers}</div>
                                <div className="stat-label">Teachers (Staff)</div>
                            </div>

                            <div className="stat-card success">
                                <div className="stat-icon">✓</div>
                                <div className="stat-value">{stats.checkedIn}</div>
                                <div className="stat-label">Checked In</div>
                            </div>

                            <div className="stat-card pending">
                                <div className="stat-icon">⏱</div>
                                <div className="stat-value">{stats.pending}</div>
                                <div className="stat-label">Pending</div>
                            </div>

                            <div className="stat-card rate">
                                <div className="stat-icon">📈</div>
                                <div className="stat-value">{stats.checkInRate}%</div>
                                <div className="stat-label">Parent Check-in Rate</div>
                            </div>
                        </div>

                        <div className="download-stats-section">
                            <button className="download-stats-button" onClick={downloadStatsCSV}>
                                📥 Download All Data (CSV)
                            </button>
                        </div>

                        <div className="recent-checkins">
                            <h3>Recent Check-ins</h3>
                            {registrations
                                .filter(r => r.checkedIn)
                                .slice(0, 10)
                                .map((registration) => (
                                    <div key={registration.id} className="recent-item">
                                        <span className="recent-name">{registration.parentName}</span>
                                        <span className="recent-time">
                                            {new Date(registration.checkedInAt).toLocaleTimeString()}
                                        </span>
                                    </div>
                                ))}
                            {registrations.filter(r => r.checkedIn).length === 0 && (
                                <p className="no-data">No check-ins yet</p>
                            )}
                        </div>
                    </motion.div>
                )}

                {activeTab === 'luckydraw' && (
                    <motion.div
                        key="luckydraw"
                        className="tab-content"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                    >
                        <div className="lucky-draw-container">
                            {/* Confetti Animation */}
                            {showConfetti && (
                                <div className="confetti-container">
                                    {Array.from({ length: 100 }).map((_, i) => (
                                        <motion.div
                                            key={i}
                                            className="confetti"
                                            initial={{
                                                x: window.innerWidth / 2,
                                                y: -20,
                                                rotate: 0,
                                                opacity: 1
                                            }}
                                            animate={{
                                                x: Math.random() * window.innerWidth,
                                                y: window.innerHeight + 100,
                                                rotate: Math.random() * 720,
                                                opacity: [1, 1, 0]
                                            }}
                                            transition={{
                                                duration: 3 + Math.random() * 2,
                                                ease: "easeOut"
                                            }}
                                            style={{
                                                backgroundColor: ['#FF6B35', '#FDB750', '#C2185B', '#5B2C6F', '#1D7754'][i % 5]
                                            }}
                                        />
                                    ))}
                                </div>
                            )}

                            <div className="lucky-draw-header">
                                <h2>🎁 Lucky Draw System</h2>
                                <p className="lucky-draw-subtitle">Pick random winners with fairness and excitement!</p>
                            </div>

                            {/* Filter Options */}
                            <div className="filter-section">
                                <h3>Select Participants</h3>
                                <div className="filter-buttons">
                                    <motion.button
                                        className={`filter-btn ${luckyDrawFilter === 'all' ? 'active' : ''}`}
                                        onClick={() => setLuckyDrawFilter('all')}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <span className="filter-icon">👥</span>
                                        <span>All ({registrations.length - winners.length})</span>
                                    </motion.button>

                                    <motion.button
                                        className={`filter-btn ${luckyDrawFilter === 'parents' ? 'active' : ''}`}
                                        onClick={() => setLuckyDrawFilter('parents')}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <span className="filter-icon">👨‍👩‍👧</span>
                                        <span>Parents Only ({registrations.filter(r => !r.isTeacher && !winners.find(w => w.id === r.id)).length})</span>
                                    </motion.button>

                                    <motion.button
                                        className={`filter-btn ${luckyDrawFilter === 'teachers' ? 'active' : ''}`}
                                        onClick={() => setLuckyDrawFilter('teachers')}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <span className="filter-icon">👩‍🏫</span>
                                        <span>Teachers Only ({registrations.filter(r => r.isTeacher && r.type === 'carnival' && !winners.find(w => w.id === r.id)).length})</span>
                                    </motion.button>

                                    <motion.button
                                        className={`filter-btn ${luckyDrawFilter === 'stallowners' ? 'active' : ''}`}
                                        onClick={() => setLuckyDrawFilter('stallowners')}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <span className="filter-icon">🎪</span>
                                        <span>Stall Owners ({registrations.filter(r => r.type === 'stallowner' && !winners.find(w => w.id === r.id)).length})</span>
                                    </motion.button>
                                </div>
                            </div>

                            {/* Shuffle Display */}
                            <div className="shuffle-display">
                                <AnimatePresence mode="wait">
                                    {showAllCards ? (
                                        <div key="all-cards" className="all-cards-container">
                                            <motion.div
                                                className="all-cards-header"
                                                initial={{ opacity: 0, y: -20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                            >
                                                <h3>📋 All Participants ({allParticipantCards.length})</h3>
                                                {!isShuffling && <p>Verify everyone is here, then click "Start Shuffle!" to begin!</p>}
                                                {isShuffling && showCardBacks && !pickedCardId && (
                                                    <motion.p
                                                        className="shuffle-status-text"
                                                        animate={{ opacity: [0.6, 1, 0.6] }}
                                                        transition={{ duration: 1, repeat: Infinity }}
                                                    >
                                                        🎲 Shuffling the deck...
                                                    </motion.p>
                                                )}
                                                {pickedCardId && !revealingWinner && (
                                                    <motion.p
                                                        className="shuffle-status-text"
                                                        animate={{ scale: [1, 1.05, 1] }}
                                                        transition={{ duration: 0.8, repeat: Infinity }}
                                                    >
                                                        ✨ A card has been chosen...
                                                    </motion.p>
                                                )}
                                                {revealingWinner && (
                                                    <motion.p
                                                        className="shuffle-status-text"
                                                        animate={{ scale: [1, 1.08, 1] }}
                                                        transition={{ duration: 0.6, repeat: Infinity }}
                                                    >
                                                        🎉 And the winner is...
                                                    </motion.p>
                                                )}
                                            </motion.div>

                                            <div className="all-cards-grid">
                                                {allParticipantCards.map((participant, index) => {
                                                    const isPicked = pickedCardId === participant.id;
                                                    const shouldFlip = showCardBacks && !isPicked;
                                                    const shouldReveal = isPicked && revealingWinner;

                                                    return (
                                                        <motion.div
                                                            key={participant.id}
                                                            className={`participant-card-flip ${isPicked ? 'picked' : ''}`}
                                                            layout="position"
                                                            initial={{ scale: 0, rotate: -180 }}
                                                            animate={{
                                                                scale: isPicked ? 1.4 : 1,
                                                                rotate: isPicked ? 0 : 0,
                                                                y: isPicked ? -50 : 0,
                                                                zIndex: isPicked ? 100 : 1
                                                            }}
                                                            transition={{
                                                                delay: !isShuffling ? index * 0.03 : 0,
                                                                type: "spring",
                                                                stiffness: isPicked ? 200 : 260,
                                                                damping: isPicked ? 25 : 20,
                                                                layout: {
                                                                    type: "spring",
                                                                    stiffness: 60,
                                                                    damping: 12,
                                                                    mass: 1.2,
                                                                    bounce: 0.4
                                                                }
                                                            }}
                                                        >
                                                            <motion.div
                                                                className="card-inner"
                                                                animate={{
                                                                    rotateY: shouldFlip ? 180 : (shouldReveal ? 0 : (showCardBacks && isPicked ? 180 : 0))
                                                                }}
                                                                transition={{
                                                                    duration: isPicked && revealingWinner ? 2 : (showCardBacks ? 1.2 : 0.8),
                                                                    ease: "easeInOut"
                                                                }}
                                                            >
                                                                {/* Card Front */}
                                                                <div className="card-face card-front">
                                                                    <div className="card-number">#{index + 1}</div>
                                                                    <div className="card-content">
                                                                        <div className="card-name">{participant.parentName}</div>
                                                                        <div className="card-child">{participant.childName}</div>
                                                                        {participant.isTeacher && (
                                                                            <div className="card-teacher-badge">👩‍🏫</div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Card Back */}
                                                                <div className="card-face card-back">
                                                                    <div className="card-back-pattern">
                                                                        <img
                                                                            src={AbhyaasLogo}
                                                                            alt="Abhyaas"
                                                                            className="card-back-logo-img"
                                                                        />
                                                                        <div className="card-back-text">Abhyaas</div>
                                                                        <div className="card-back-subtext">Lucky Draw</div>
                                                                    </div>
                                                                </div>
                                                            </motion.div>
                                                        </motion.div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : winner ? (
                                        <motion.div
                                            key="winner"
                                            className="shuffle-card winner-card"
                                            initial={{ scale: 0, rotate: -180 }}
                                            animate={{ scale: 1, rotate: 0 }}
                                            transition={{ type: "spring", duration: 0.8 }}
                                        >
                                            <motion.div
                                                className="winner-crown"
                                                animate={{ rotate: [-5, 5, -5] }}
                                                transition={{ duration: 1, repeat: Infinity }}
                                            >
                                                👑
                                            </motion.div>
                                            <div className="winner-label">🎉 WINNER! 🎉</div>
                                            <div className="winner-name">{winner.parentName}</div>
                                            <div className="winner-details">
                                                {winner.type === 'stallowner' ? (
                                                    <>
                                                        <p>Stall: {winner.childName}</p>
                                                        <p>Phone: {winner.phone}</p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p>Child: {winner.childName}</p>
                                                        <p>Phone: {winner.phone}</p>
                                                        <p className="winner-id">ID: {winner.registrationId}</p>
                                                    </>
                                                )}
                                            </div>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key="ready"
                                            className="shuffle-card ready-card"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                        >
                                            <div className="ready-icon">🎁</div>
                                            <div className="ready-text">Ready to pick a winner!</div>
                                            <div className="ready-subtext">
                                                {getEligibleParticipants().length} eligible participants
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Action Buttons */}
                            <div className="lucky-draw-actions">
                                <motion.button
                                    className="draw-button"
                                    onClick={startLuckyDraw}
                                    disabled={isShuffling || getEligibleParticipants().length === 0}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <span className="button-icon">{showAllCards ? '🎲' : '👀'}</span>
                                    <span>{showAllCards ? 'Start Shuffle!' : 'Show All Cards'}</span>
                                </motion.button>

                                {winners.length > 0 && (
                                    <motion.button
                                        className="reset-button"
                                        onClick={resetWinners}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <span className="button-icon">🔄</span>
                                        <span>Reset All Winners</span>
                                    </motion.button>
                                )}
                            </div>

                            {/* Winners List */}
                            {winners.length > 0 && (
                                <div className="winners-list-section">
                                    <h3>🏆 Winners List ({winners.length})</h3>
                                    <div className="winners-list">
                                        {winners.map((w, index) => (
                                            <motion.div
                                                key={w.id}
                                                className="winner-item"
                                                initial={{ opacity: 0, x: -50 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: index * 0.1 }}
                                            >
                                                <div className="winner-number">#{index + 1}</div>
                                                <div className="winner-info">
                                                    <div className="winner-name-small">{w.parentName}</div>
                                                    <div className="winner-child">
                                                        {w.type === 'stallowner' ? 'Stall: ' : 'Child: '}{w.childName}
                                                    </div>
                                                </div>
                                                <button
                                                    className="remove-winner-btn"
                                                    onClick={() => removeWinner(w.id)}
                                                    title="Remove from winners"
                                                >
                                                    ×
                                                </button>
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminDashboard;
